// Match Card Styles
// Styling for single-card Matches tab view

import styled, { css, keyframes } from "styled-components";
import { theme } from "../theme";

// Subtle animations for button interactions
const heartPop = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); }
`;

const buttonPulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
`;

const checkmarkAppear = keyframes`
  0% { transform: scale(0) rotate(-90deg); opacity: 0; }
  50% { transform: scale(1.1) rotate(0deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

// Match Card Container - Full viewport height card with snap
// Height calculated to fill entire viewport - tabs overlay transparently at top
export const MatchCardContainer = styled.div`
  position: relative;
  width: 100%;
  /* Use 100% to fill parent container (MatchesScrollContainer) instead of viewport units */
  /* This ensures cards match the container height exactly and prevents white space issues */
  height: 100%;
  min-height: 100%;
  max-height: 100%;
  background: ${theme.colors.background.card};
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  max-width: 100%;
  scroll-snap-align: start;
  scroll-snap-stop: always;
  /* Ensure no margin or gap that would allow next card to peek */
  margin-bottom: 0;
  flex-shrink: 0;
  /* Improve scroll performance */
  will-change: transform;
  transform: translateZ(0);
`;

// Photo Section - 100% of card (full height, no content section)
export const MatchCardPhoto = styled.div<{ $photoUrl: string }>`
  position: relative;
  width: 100%;
  height: 100%;  // Changed from 75% to 100%
  min-height: 100vh;  // Changed from 450px to full viewport height
  background: #000;
  border-radius: 0;  // Changed from 20px 20px 0 0 to 0 (full card, no border radius)
  overflow: hidden;
  /* Ensure media displays edge-to-edge */
  margin: 0;
  padding: 0;
  left: 0;
  right: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    min-height: 100dvh;  // Changed from 375px to full dynamic viewport height
    border-radius: 0;  // Changed from 16px 16px 0 0 to 0
  }
`;

// Photo Overlay for Quick Info
export const PhotoOverlay = styled.div`
  position: absolute;
  bottom: 0; /* Extend overlay to bottom of container */
  left: 0;
  right: 0; /* Full width - edge to edge */
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 100%);
  padding: 1rem 1.25rem;
  padding-right: 80px; /* Add padding to text content to avoid button overlap */
  padding-bottom: calc(1rem + 70px + env(safe-area-inset-bottom, 0px)); /* Keep content position same */
  color: white !important;
  z-index: 15;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  /* Ensure all text inside is white */
  * {
    color: white !important;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.875rem 1rem;
    padding-right: 70px; /* Smaller padding on mobile to avoid button overlap */
    padding-bottom: calc(0.875rem + 70px + env(safe-area-inset-bottom, 0px)); /* Keep content position same */
  }

  @media (min-width: ${theme.breakpoints.tablet}) {
    padding: 1.25rem 1.5rem;
    padding-right: 90px; /* Larger padding on desktop to avoid button overlap */
    padding-bottom: calc(1.25rem + 70px + env(safe-area-inset-bottom, 0px)); /* Keep content position same */
  }
`;

// Content Section - 25% of card, no scrolling, snaps to bottom
export const MatchCardContent = styled.div`
  flex: 1;
  padding: 1rem 1.25rem 1.5rem 1.25rem;
  overflow: visible;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  background: ${theme.colors.background.card};
  justify-content: space-between;
  min-height: 0; /* Allows flex child to shrink */
  /* Ensure content at bottom is not clipped */
  padding-bottom: 1.5rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.875rem 1rem 1.25rem 1rem;
    padding-bottom: 1.25rem;
    gap: 0.625rem;
  }
`;

// Header Elements
export const MatchCardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

export const MatchCardName = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: white !important;
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5), 0 0 2px rgba(0, 0, 0, 0.5);

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

export const MatchCardInfo = styled.div`
  font-size: 0.875rem;
  color: white !important;
  margin-top: 0.25rem;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.5), 0 0 2px rgba(0, 0, 0, 0.5);

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
`;

// Compatibility Badge
export const MatchCardBadge = styled.div<{ $color: string }>`
  align-self: flex-start; /* Position at start of overlay flex container */
  background: ${({ $color }) => $color};
  color: white !important;
  padding: 0.375rem 0.75rem;
  border-radius: 16px;
  font-size: 0.75rem;
  font-weight: 700;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
  margin-bottom: 0.25rem;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.3rem 0.625rem;
    font-size: 0.7rem;
    border-radius: 12px;
  }
`;

// Right Action Buttons Container - Vertical stack on right edge
export const MatchCardRightActions = styled.div`
  position: absolute;
  right: 16px;
  bottom: calc(120px + env(safe-area-inset-bottom, 0px)); /* Positioned above bottom overlay */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  z-index: 20;
  pointer-events: none; /* Allow clicks to pass through container */
  background: transparent; /* Ensure container is transparent */
  padding: 0;
  margin: 0;

  @media (max-width: ${theme.breakpoints.mobile}) {
    right: 12px;
    bottom: calc(110px + env(safe-area-inset-bottom, 0px));
    gap: 20px;
    /* Account for safe area on devices with notches */
    padding-right: env(safe-area-inset-right, 0px);
  }

  @media (min-width: ${theme.breakpoints.tablet}) {
    right: 20px;
    bottom: calc(130px + env(safe-area-inset-bottom, 0px));
    gap: 28px;
  }
`;

// Base Action Button - Circular button with backdrop blur
export const MatchCardActionButton = styled.button<{ $isActive?: boolean }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  border: 2px solid rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(10px);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  pointer-events: auto; /* Re-enable clicks on buttons */
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  padding: 0;
  margin: 0;

  /* Ensure SVG icons are properly sized and visible */
  svg {
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    /* Let component props control stroke and fill */
  }

  &:hover:not(:disabled) {
    transform: scale(1.1);
    opacity: 1;
    border-color: rgba(255, 255, 255, 0.8);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 44px;
    height: 44px;
    border-width: 1.5px;
    
    svg {
      width: 22px;
      height: 22px;
    }
  }

  @media (min-width: ${theme.breakpoints.tablet}) {
    width: 56px;
    height: 56px;
    
    svg {
      width: 28px;
      height: 28px;
    }
  }
`;

// Like Button - Red when liked, white when not
export const MatchCardLikeButton = styled(MatchCardActionButton)<{ $isLiked: boolean; $justLiked?: boolean }>`
  ${({ $isLiked }) => $isLiked && css`
    background: rgba(236, 100, 53, 0.9);
    border-color: rgba(236, 100, 53, 1);
    color: white;
    
    svg {
      fill: currentColor;
      stroke: currentColor;
      color: white;
    }
  `}

  ${({ $isLiked }) => !$isLiked && css`
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
    
    svg {
      fill: none;
      stroke: currentColor;
      color: white;
    }
  `}

  ${({ $justLiked }) => $justLiked && css`
    animation: ${heartPop} 0.4s ease;
  `}
`;

// Pass Button — red once the pass is recorded.
//
// It used to be permanently white, so pressing it produced no visible change
// at all: the swipe reached the database, the signal was tracked, and the UI
// said nothing. With every other action on this card confirming itself (the
// heart fills, the deck button turns green with a tick), an X that never moves
// reads as a button that did not work.
export const MatchCardPassButton = styled(MatchCardActionButton)<{
  $isPassed?: boolean;
  $justPassed?: boolean;
}>`
  ${({ $isPassed }) => $isPassed && css`
    background: rgba(231, 76, 60, 0.9);
    border-color: rgba(231, 76, 60, 1);
    color: white;
  `}

  ${({ $isPassed }) => !$isPassed && css`
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
  `}

  /* Ensure SVG icon is visible */
  svg {
    stroke: currentColor;
    color: white;
  }

  ${({ $justPassed }) => $justPassed && css`
    animation: ${heartPop} 0.4s ease;
  `}
`;

// Save to Deck Button - Primary color when saved
export const MatchCardSaveButton = styled(MatchCardActionButton)<{ $isSaved: boolean; $justSaved?: boolean }>`
  ${({ $isSaved }) => $isSaved && css`
    background: rgba(46, 204, 113, 0.9);
    border-color: rgba(46, 204, 113, 1);
    color: white;
  `}

  ${({ $isSaved }) => !$isSaved && css`
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.5);
    color: white;
  `}

  ${({ $justSaved }) => $justSaved && css`
    animation: ${checkmarkAppear} 0.5s ease;
  `}
`;

// View Profile Button - Always white/transparent
export const MatchCardViewProfileButton = styled(MatchCardActionButton)`
  /* Uses base styles - no special styling needed */
`;

// Horizontal container for "Why you might connect" and "Shared Interests" sections
export const MatchCardSectionsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  gap: 1rem;
  width: 100%;
  margin-bottom: 0.75rem;

  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    flex-wrap: wrap;
    gap: 0.75rem;
  }
`;

// Content Sections - Compact (updated for horizontal layout)
export const MatchCardSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  flex: 1 1 0;  // flex-grow: 1, flex-shrink: 1, flex-basis: 0 (equal width distribution)
  min-width: 0;  // Prevent flex items from overflowing
  width: 0;  // Force flex to calculate width based on flex-basis

  @media (max-width: ${theme.breakpoints.mobile}) {
    width: 100%;  // Full width on mobile
    flex: 1 1 auto;  // Reset flex on mobile
  }
`;

export const MatchCardTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  margin: 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.7rem;
  }
`;

export const MatchCardReasons = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

export const MatchCardReason = styled.li`
  font-size: 0.85rem;
  color: ${theme.colors.text.primary};
  line-height: 1.3;

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
  }
`;

// Interests - Compact
export const MatchCardInterests = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem;
  margin-top: 0;
`;

export const MatchCardInterestTag = styled.span`
  background: ${theme.colors.background.secondary};
  color: ${theme.colors.text.primary};
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 500;
  border: 1px solid ${theme.colors.border.light};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.7rem;
    padding: 0.2rem 0.5rem;
  }
`;

// Conversation Starter
export const MatchCardConversationStarter = styled.div`
  background: ${theme.colors.background.secondary};
  padding: 0.75rem 1rem;
  border-radius: 12px;
  font-size: 0.9rem;
  color: ${theme.colors.text.primary};
  font-style: italic;
  border-left: 3px solid ${theme.colors.primary};

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.85rem;
    padding: 0.625rem 0.875rem;
  }
`;

// Action Buttons - Always at bottom
export const MatchCardActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  margin-top: auto;
  padding-top: 0.5rem;
  padding-bottom: 0.25rem;
  /* Ensure buttons and links are fully visible */
  min-height: fit-content;
  overflow: visible;
`;

export const AddToDeckButton = styled.button<{ $isAdded: boolean }>`
  width: 100%;
  padding: 1rem;
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  /* Consistent color for all buttons - primary brand color */
  background: ${({ $isAdded }) =>
    $isAdded 
      ? "#2ecc71" 
      : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary}dd 100%)`};
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.3);
    background: ${({ $isAdded }) =>
      $isAdded 
        ? "#27ae60" 
        : `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primary} 100%)`};
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.8;
    cursor: not-allowed;
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0.875rem;
    font-size: 0.9rem;
  }
`;

export const ViewProfileLink = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.primary};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0.5rem 0.5rem 1rem 0.5rem;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-thickness: 1px;
  transition: all 0.2s ease;
  /* Ensure text and underline are fully visible */
  line-height: 1.5;
  display: block;
  white-space: nowrap;
  /* Ensure underline is not clipped */
  overflow: visible;
  position: relative;

  &:hover {
    color: ${theme.colors.text.primary};
  }

  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 0.8rem;
    padding: 0.5rem 0.5rem 0.875rem 0.5rem;
    text-underline-offset: 2px;
  }
`;

